import { CarbonEmissionsSummary } from '@/apis/types';
import { formatKgCo2e } from '@/utils/formatEmissions';
import { Tooltip } from 'antd';
import React from 'react';
import { CustomShipmentText } from '../../../components';
import styles from './ShipmentEmissionsSection.module.css';

interface ShipmentEmissionsSectionProps {
  emissions?: CarbonEmissionsSummary;
}

const FIGURE_STATES = [
  'per_leg',
  'whole_journey_proxy',
  'whole_journey_ground',
];

/**
 * Renders the shipment's emissions estimate as one field row in the detail
 * drawer, at the weight of Reference ID beside it.
 *
 * When there is a figure, the figure is all there is, plus a one-line source
 * when a movement has one: an IATA flight, a US DOT airline average, or a GLEC
 * rate with the reason. Movements that used different sources each get a line,
 * labelled by route. The API still returns the
 * movement breakdown, the well-to-wheel split, and the method disclosure, and
 * none of that is shown yet — surfacing it is a render change here, not a
 * change to the contract.
 *
 * The states with no figure keep the row and show a short placeholder, with the
 * reason behind a tooltip. Both strings come from the API, so the drawer cannot
 * describe a result in words the committed method does not allow, and a row
 * that vanished for those shipments would read as broken rather than honest.
 */
const ShipmentEmissionsSection: React.FC<ShipmentEmissionsSectionProps> = ({
  emissions,
}) => {
  if (!emissions) return null;

  const hasFigure = FIGURE_STATES.includes(emissions.display_state);
  const sourced = (emissions.legs ?? [])
    .map((leg) => ({
      leg,
      line: leg.source_line || leg.flight?.source_line || '',
    }))
    .filter(({ line }) => line);
  // One line when every movement used the same source; otherwise one per
  // movement, so a mixed shipment never reads as if it used a single source.
  const sourceLines = sourced.every(({ line }) => line === sourced[0].line)
    ? sourced.slice(0, 1).map(({ line }) => line)
    : sourced.map(({ leg, line }) => {
        const route = [leg.from?.iata_code, leg.to?.iata_code];
        return route.every(Boolean) ? `${route.join(' → ')}: ${line}` : line;
      });

  return (
    <div data-testid='shipment-emissions-section'>
      <CustomShipmentText
        text={emissions.label}
        fontWeight={600}
        marginBottom='4px'
      />

      {hasFigure ? (
        <div data-testid='emissions-wtw-total'>
          <CustomShipmentText
            text={formatKgCo2e(emissions.totals.co2e_wtw_kg) ?? '--'}
            color='var(--text-light)'
            style={{ fontVariantNumeric: 'tabular-nums' }}
          />
          {sourceLines.map((line) => (
            <span
              key={line}
              data-testid='emissions-source-line'
              className={styles.sourceLine}
            >
              {line}
            </span>
          ))}
        </div>
      ) : (
        <Tooltip
          title={emissions.message}
          placement='bottomLeft'
          // antd defaults to 14px, larger than the 12px row it explains. This
          // note is subordinate to the row, so it reads a step smaller.
          overlayStyle={{ maxWidth: '220px' }}
          overlayInnerStyle={{
            fontSize: '11px',
            lineHeight: '15px',
            padding: '6px 8px',
            minHeight: 'auto',
          }}
        >
          <span
            data-testid='emissions-value-note'
            tabIndex={0}
            className={styles.valueNote}
          >
            {emissions.value_label}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default ShipmentEmissionsSection;
